import { zodResolver } from "@hookform/resolvers/zod";
import { HTTPError } from "@marketplace/http";
import { notifications } from "@marketplace/ui/notifications";
import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { useForm, type UseFormReturn } from "react-hook-form";
import { useTranslation } from "react-i18next";

import {
  usePostGeneratePresignedUrlMutation,
  usePostUploadMediaMutation
} from "@/hooks";

import type { CreateCrmProductRequestDto } from "../../dtos/crm-product-dto";
import { ProductQueryKeys } from "../../generators/product-query-keys";
import { usePostCreateCrmProductMutation } from "../mutations/use-post-create-crm-product-mutation";
import { usePutUpdateCrmProductMutation } from "../mutations/use-put-update-crm-product-mutation";
import { useGetCrmProductDetailQuery } from "../queries/use-get-crm-product-detail-query";
import {
  type CrmProductFormSchema,
  useCrmProductFormSchema
} from "../schemas/use-crm-product-form-schema";

type UseCrmProductFormProps = {
  productId: string;
  productName: string;
  onSuccess?: () => void;
};

type UseCrmProductFormReturn = {
  formMethods: UseFormReturn<CrmProductFormSchema>;
  onSubmit: (e?: React.BaseSyntheticEvent) => Promise<void>;
  isPending: boolean;
  isLoadingDetail: boolean;
  handleMediaFileChange: (file: File | null) => Promise<void>;
  isMediaPresignedLoading: boolean;
};

export const useCrmProductForm = ({
  productId,
  productName,
  onSuccess
}: UseCrmProductFormProps): UseCrmProductFormReturn => {
  const { t } = useTranslation("product");
  const queryClient = useQueryClient();
  const schema = useCrmProductFormSchema();

  const formMethods = useForm<CrmProductFormSchema>({
    resolver: zodResolver(schema),
    defaultValues: {
      crm_product_id: "",
      attributes: [{ key: "", value: "" }],
      media_url: "",
      media_file: undefined,
      hierarchy_name_level1: "",
      hierarchy_name_level2: "",
      hierarchy_name_level3: "",
      hierarchy_name_level4: ""
    }
  });

  const {
    data: detailData,
    isLoading: isLoadingDetail,
    error: detailError
  } = useGetCrmProductDetailQuery({
    productId,
    options: {
      enabled: !!productId,
      retry: false
    }
  });

  const isExisting = !!detailData?.data && !detailError;

  // Populate form with existing data
  useEffect(() => {
    if (detailData?.data && !detailError) {
      const {
        crm_product_id,
        attributes,
        media_url,
        hierarchy_name_level1,
        hierarchy_name_level2,
        hierarchy_name_level3,
        hierarchy_name_level4
      } = detailData.data;

      formMethods.reset({
        crm_product_id,
        attributes:
          attributes?.map((attr) => {
            const key = Object.keys(attr)[0] || "";
            return { key, value: attr[key] || "" };
          }) || [],
        media_url: media_url || "",
        media_file: undefined,
        hierarchy_name_level1: hierarchy_name_level1 || "",
        hierarchy_name_level2: hierarchy_name_level2 || "",
        hierarchy_name_level3: hierarchy_name_level3 || "",
        hierarchy_name_level4: hierarchy_name_level4 || ""
      });
    }
  }, [detailData, detailError, formMethods]);

  // Clear form on 404
  useEffect(() => {
    if (detailError && detailError instanceof HTTPError) {
      if (detailError.response.status === 404) {
        formMethods.reset({
          crm_product_id: "",
          attributes: [{ key: "", value: "" }],
          media_url: "",
          media_file: undefined,
          hierarchy_name_level1: "",
          hierarchy_name_level2: "",
          hierarchy_name_level3: "",
          hierarchy_name_level4: ""
        });
      }
    }
  }, [detailError, formMethods]);

  const uploadMediaMutation = usePostUploadMediaMutation();
  const { mutateAsync: mutateGeneratePresignedUrlAsync } =
    usePostGeneratePresignedUrlMutation();
  const [tempMediaPath, setTempMediaPath] = useState<string | null>(null);
  const [isMediaPresignedLoading, setIsMediaPresignedLoading] = useState(false);

  const createMutation = usePostCreateCrmProductMutation({
    options: {
      onSuccess: () => {
        notifications.show({
          message: t(
            "integrationProductListPage.crmModal.notifications.successCreate"
          )
        });
        queryClient.invalidateQueries({
          queryKey: ProductQueryKeys._def
        });
        formMethods.reset();
        onSuccess?.();
      },
      onError: (error) => {
        if (error instanceof HTTPError) {
          notifications.show({
            variant: "error",
            message: t(
              "integrationProductListPage.crmModal.notifications.failedToCreate"
            )
          });
        }
      }
    }
  });

  const updateMutation = usePutUpdateCrmProductMutation({
    options: {
      onSuccess: () => {
        notifications.show({
          message: t(
            "integrationProductListPage.crmModal.notifications.successUpdate"
          )
        });
        queryClient.invalidateQueries({
          queryKey: ProductQueryKeys._def
        });
        formMethods.reset();
        onSuccess?.();
      },
      onError: (error) => {
        if (error instanceof HTTPError) {
          notifications.show({
            variant: "error",
            message: t(
              "integrationProductListPage.crmModal.notifications.failedToUpdate"
            )
          });
        }
      }
    }
  });

  const handleMediaFileChange = async (file: File | null) => {
    if (!file) return;

    formMethods.setValue("media_file", file);
    setIsMediaPresignedLoading(true);

    try {
      const response = await mutateGeneratePresignedUrlAsync({
        mime_type: file.type,
        size: file.size,
        type: "image"
      });

      if (response?.data?.media_path) {
        setTempMediaPath(response.data.media_path);
      }
    } catch {
      notifications.show({
        variant: "error",
        message: t(
          "integrationProductListPage.crmModal.notifications.failedToGeneratePresigned"
        )
      });
    } finally {
      setIsMediaPresignedLoading(false);
    }
  };

  const onSubmit = formMethods.handleSubmit(async (data) => {
    let mediaUrl = data.media_url || "";

    // Upload media file if provided in the background
    if (data.media_file && tempMediaPath) {
      uploadMediaMutation
        .mutateAsync({
          type: "image",
          object_name: "product",
          object_type: "product",
          file: data.media_file,
          reference_no: tempMediaPath.split("/").pop()
        })
        .catch((err) =>
          console.error("Background CRM product media upload failed:", err)
        );

      mediaUrl = tempMediaPath;
    } else if (tempMediaPath) {
      mediaUrl = tempMediaPath;
    }

    const attributes = (data.attributes || []).map((attr) => ({
      [attr.key]: attr.value
    }));

    const payload: CreateCrmProductRequestDto = {
      lampu_product_id: productId,
      product_name: productName,
      crm_product_id: data.crm_product_id,
      attributes,
      media_url: mediaUrl,
      hierarchy_name_level1: data.hierarchy_name_level1 || undefined,
      hierarchy_name_level2: data.hierarchy_name_level2 || undefined,
      hierarchy_name_level3: data.hierarchy_name_level3 || undefined,
      hierarchy_name_level4: data.hierarchy_name_level4 || undefined
    };

    if (isExisting) {
      await updateMutation.mutateAsync({
        ...payload,
        productId
      });
    } else {
      await createMutation.mutateAsync(payload);
    }
  });

  return {
    formMethods,
    onSubmit,
    isPending:
      createMutation.isPending ||
      updateMutation.isPending ||
      uploadMediaMutation.isPending ||
      isMediaPresignedLoading,
    isLoadingDetail,
    handleMediaFileChange,
    isMediaPresignedLoading
  };
};
